import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { CaretLeftIcon, CaretRightIcon, CaretUpIcon, CaretDownIcon, CheckIcon } from "@phosphor-icons/react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import usePagination from "@/hooks/usePagination";
import Pager from "@/components/Pager";

/*
  The shared grid for every table in the app: a card whose header is a toolbar
  (a title + row count, or a search box via `toolbarStart`; a column picker; a
  compact pager), plus resizable and reorderable columns, wrapping cells,
  client-side pagination and a "Showing x-y of z" footer.

  Column model:
    key             unique; also the identity used when persisting layout
    label           name shown in the column picker
    head            header text when it should differ from `label` (optional)
    locked          can't be hidden or dragged — give this to the identity column
    hideFromPicker  renders, but never listed in the picker (checkbox/action cells)
    defaultHidden   starts switched off; the picker brings it back
    weight          relative starting width. Columns are seeded proportionally to
                    fill the container exactly, so nothing scrolls until the user
                    drags a column wider. Defaults to DEFAULT_WEIGHT.
    minWidth        floor in px below which this column won't shrink, whether from
                    proportional seeding (more visible columns squeezing its share)
                    or a manual resize drag. Leave unset for the ordinary MIN_COL_PX
                    floor every column gets. Give this to a locked/sticky identity
                    column so revealing more columns can't squeeze it illegible.
    cls             padding / text-align for BOTH header and body cell.
                    Do NOT put a width here — widths are state now, because a
                    resizable column can't have its width pinned by a class.
    align           vertical-align for the body cell (default align-top, which
                    is what wrapped text wants)
    cell            (row, index) => ReactNode

  Text wraps. Cells must NOT use `truncate` or `whitespace-nowrap` — wrapping is
  the whole point, and a nowrap cell in a narrow column also spills sideways over
  its neighbour.

  An earlier version of this file derived widths by regex-parsing Tailwind classes
  like `w-[124px]`. Don't reintroduce that: the escaping silently broke, the
  parser returned 0 for everything, and every column came out the same width with
  nothing failing loudly.
*/

export const DEFAULT_WEIGHT = 200;
const MIN_COL_PX = 64;

// Layout is one object per table: which columns show, what order they're in, and
// how wide each is. Persisted together so a drag and a resize survive a reload.
// Reads and writes are wrapped because storage throws outright in a private
// window, and a grid that won't render beats a remembered layout.
const readLayout = (storageKey) => {
  if (!storageKey) return null;
  try {
    const raw = JSON.parse(localStorage.getItem(storageKey) || "null");
    if (Array.isArray(raw)) return { visible: raw };      // pre-layout format
    if (raw && typeof raw === "object") return raw;
  } catch {
    /* unreadable storage */
  }
  return null;
};

const writeLayout = (storageKey, layout) => {
  if (!storageKey) return;
  try { localStorage.setItem(storageKey, JSON.stringify(layout)); } catch { /* ignore */ }
};

export default function DataTable({
  title,
  rows,
  columns,
  rowKey = (row, i) => row._id ?? row.id ?? i,
  onRowClick,
  rowClassName,
  pageSize = 20,
  storageKey,
  testIdPrefix = "table",
  emptyTitle = "Empty",
  emptyMessage = "Nothing to show.",
  emptyTestId,
  resizable = true,
  reorderable = true,
  // Two ways to render a cell: a `cell` function on the column, or one
  // `cellFor(key, row, i)` for the whole table. The second suits a page that
  // already had a single switch over column keys.
  cellFor,
  // Replaces the title + row-count block on the left of the toolbar. Use it
  // when a grid wants a search box there instead of a heading — the row count
  // is still shown in the footer, so nothing is lost.
  toolbarStart = null,
  toolbarExtra = null,
  footerExtra = null,
}) {
  const saved = useRef(readLayout(storageKey)).current;

  const [visibleCols, setVisibleCols] = useState(() => {
    if (saved?.visible?.length) {
      // Intersect with the current columns: a column deleted from the code must
      // not resurrect, and a locked one comes back even if saved as hidden.
      //
      // A column added to the code AFTER the layout was saved is a third case,
      // and it used to lose: absent from saved.visible, it stayed hidden for
      // everyone who had ever used the grid, so shipping a new column made it
      // invisible to exactly the people who use the page most. saved.order
      // lists every key that existed when the layout was written, so a key
      // missing from it is new — show it unless it asked to start hidden.
      const knownThen = saved.order || saved.visible;
      const valid = columns
        .filter((c) =>
          c.locked ||
          c.hideFromPicker ||
          saved.visible.includes(c.key) ||
          (!knownThen.includes(c.key) && !c.defaultHidden))
        .map((c) => c.key);
      if (valid.length) return valid;
    }
    return columns.filter((c) => !c.defaultHidden).map((c) => c.key);
  });

  const [order, setOrder] = useState(() => {
    const keys = columns.map((c) => c.key);
    if (!saved?.order?.length) return keys;
    // Saved order first (dropping unknown keys), then anything new appended so a
    // column added later still appears.
    const kept = saved.order.filter((k) => keys.includes(k));
    return [...kept, ...keys.filter((k) => !kept.includes(k))];
  });

  const [widths, setWidths] = useState(() => saved?.widths || {});

  /*
    Sorting.

    A column opts in by declaring `sortValue(row)` — the grid cannot infer a
    sort key from a rendered cell, which may be an avatar stack or a status
    pill. Clicking a sortable header cycles through its two directions and then
    back to unsorted, so the original order is always reachable.

    WHICH DIRECTION COMES FIRST is per column, via `sortDefaultDir`. Ascending
    is the right default for a name or a date, but not for a magnitude: the
    first click on Slippage has to put the worst overdue at the top, because
    "which of these is late" is the only question anyone opens that column to
    ask. Ascending-first would have answered it with the rows that have the
    most time still to run.

    Deliberately NOT persisted with the column layout: a remembered sort is a
    filter the reader did not set this session, and a grid that opens in some
    forgotten order is confusing.
  */
  const [sort, setSort] = useState({ key: null, dir: "asc" });

  const toggleSort = useCallback((key, firstDir = "asc") => {
    setSort((cur) => {
      if (cur.key !== key) return { key, dir: firstDir };
      // Both directions, then unsorted — whichever direction the column starts
      // in, so a descending-first column is not stuck being un-clearable.
      if (cur.dir === firstDir) return { key, dir: firstDir === "desc" ? "asc" : "desc" };
      return { key: null, dir: "asc" };
    });
  }, []);

  useEffect(() => {
    writeLayout(storageKey, { visible: visibleCols, order, widths });
  }, [storageKey, visibleCols, order, widths]);

  const byKey = Object.fromEntries(columns.map((c) => [c.key, c]));
  const visible = order.map((k) => byKey[k]).filter((c) => c && visibleCols.includes(c.key));
  const pickable = columns.filter((c) => !c.hideFromPicker);

  // Seed any column that has no width yet, sized proportionally so the visible
  // set exactly fills the container. Only unseeded columns are touched, so a
  // width the user dragged is never silently reset.
  const scrollRef = useRef(null);
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || visible.length === 0) return;
    const missing = visible.filter((c) => !widths[c.key]);
    if (missing.length === 0) return;
    const available = el.clientWidth || 0;
    if (available === 0) return;
    const seededPx = visible.reduce((n, c) => n + (widths[c.key] || 0), 0);
    const room = Math.max(available - seededPx, missing.length * MIN_COL_PX);
    const totalWeight = missing.reduce((n, c) => n + (c.weight || DEFAULT_WEIGHT), 0) || 1;
    setWidths((cur) => {
      const next = { ...cur };
      missing.forEach((c) => {
        next[c.key] = Math.max(c.minWidth || MIN_COL_PX, Math.round(((c.weight || DEFAULT_WEIGHT) / totalWeight) * room));
      });
      return next;
    });
  }, [visible, widths]);

  /* ---- resize ---- */
  const [resizingKey, setResizingKey] = useState(null);
  const dragState = useRef(null);

  const onResizeStart = useCallback((e, key) => {
    e.preventDefault();
    e.stopPropagation();
    dragState.current = { key, startX: e.clientX, startW: widths[key] || DEFAULT_WEIGHT, minWidth: byKey[key]?.minWidth };
    setResizingKey(key);
  }, [widths]);

  useEffect(() => {
    if (!resizingKey) return undefined;

    // Coalesce to one state update per animation frame. Committing on every
    // mousemove re-renders every row in the page for each event, which is what
    // made the drag feel gritty rather than smooth.
    let raf = null;
    let latestX = null;

    const commit = () => {
      raf = null;
      const d = dragState.current;
      if (!d || latestX == null) return;
      const next = Math.max(d.minWidth || MIN_COL_PX, d.startW + (latestX - d.startX));
      setWidths((cur) => (cur[d.key] === next ? cur : { ...cur, [d.key]: next }));
    };

    const onMove = (e) => {
      latestX = e.clientX;
      if (raf == null) raf = requestAnimationFrame(commit);
    };
    const onUp = () => { dragState.current = null; setResizingKey(null); };

    // Hold the resize cursor for the whole gesture — otherwise it reverts the
    // moment the pointer leaves the 9px handle — and stop the drag selecting
    // header text as it sweeps across the row.
    const prevCursor = document.body.style.cursor;
    const prevSelect = document.body.style.userSelect;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      if (raf != null) cancelAnimationFrame(raf);
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.body.style.cursor = prevCursor;
      document.body.style.userSelect = prevSelect;
    };
  }, [resizingKey]);

  /* ---- reorder ---- */
  const [dragKey, setDragKey] = useState(null);
  const [dropKey, setDropKey] = useState(null);

  const onHeaderDrop = (targetKey) => {
    if (!dragKey || dragKey === targetKey) { setDragKey(null); setDropKey(null); return; }
    setOrder((cur) => {
      const from = cur.indexOf(dragKey);
      const to = cur.indexOf(targetKey);
      if (from < 0 || to < 0 || from === to) return cur;
      const next = cur.filter((k) => k !== dragKey);
      const t = next.indexOf(targetKey);
      // Direction matters. Dragging RIGHT has to land after the target: pulling
      // the column out first shifts everything left by one, so inserting before
      // the target would drop it back exactly where it started — which is why
      // dragging one column to the right used to do nothing at all.
      next.splice(from < to ? t + 1 : t, 0, dragKey);
      return next;
    });
    setDragKey(null);
    setDropKey(null);
  };

  // Reorder without dragging. Swaps a column with its neighbour among the
  // pickable columns only, so the checkbox and action columns stay pinned where
  // they are instead of being stepped over.
  const moveCol = (key, dir) =>
    setOrder((cur) => {
      const pickKeys = cur.filter((k) => byKey[k] && !byKey[k].hideFromPicker);
      const i = pickKeys.indexOf(key);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= pickKeys.length) return cur;
      const swapped = [...pickKeys];
      [swapped[i], swapped[j]] = [swapped[j], swapped[i]];
      let p = 0;
      return cur.map((k) => (byKey[k] && !byKey[k].hideFromPicker ? swapped[p++] : k));
    });

  const pickOrder = order.filter((k) => byKey[k] && !byKey[k].hideFromPicker);

  const toggleCol = (key) =>
    setVisibleCols((cur) => (cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key]));

  const resetLayout = () => {
    setOrder(columns.map((c) => c.key));
    setWidths({});
    setVisibleCols(columns.filter((c) => !c.defaultHidden).map((c) => c.key));
  };

  const sortColumn = columns.find((c) => c.key === sort.key && typeof c.sortValue === "function");

  const sortedRows = useMemo(() => {
    if (!sortColumn) return rows;
    const dir = sort.dir === "desc" ? -1 : 1;
    const isEmpty = (v) => v === null || v === undefined || v === "";
    return [...rows].sort((a, b) => {
      const av = sortColumn.sortValue(a);
      const bv = sortColumn.sortValue(b);
      // Blanks sort last in BOTH directions: a task with no ETA is not "the
      // earliest", and flipping the arrow should not parade every empty row to
      // the top.
      if (isEmpty(av) && isEmpty(bv)) return 0;
      if (isEmpty(av)) return 1;
      if (isEmpty(bv)) return -1;
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
      // numeric:true so "Task 10" follows "Task 9" rather than preceding it.
      return String(av).localeCompare(String(bv), undefined, { sensitivity: "base", numeric: true }) * dir;
    });
  }, [rows, sortColumn, sort.dir]);

  const { page, setPage, pageCount, pageItems, totalItems, pageSize: size } = usePagination(sortedRows, pageSize);


  const shownPickable = visible.filter((c) => !c.hideFromPicker).length;

  return (
    <div className="bg-white border border-border rounded-lg shadow-sm overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-border">
        {toolbarStart || (
          <div className="flex items-baseline gap-2">
            {title && <h2 className="font-display text-lg font-bold tracking-tight">{title}</h2>}
            <span className="font-mono text-xs text-zinc-500 tabular-nums">{totalItems}</span>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          {toolbarExtra}

          {pickable.length > 1 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid={`${testIdPrefix}-column-picker`}
                  className="inline-flex items-center gap-2 h-8 px-3 rounded-sm border border-border text-xs text-zinc-600 hover:bg-zinc-50 transition-colors"
                >
                  <span className="trk-uppercase-label">Show</span>
                  <span className="font-medium">
                    {shownPickable === pickable.length ? "All columns" : `${shownPickable} of ${pickable.length}`}
                  </span>
                </button>
              </DropdownMenuTrigger>
              {/* Plain buttons rather than DropdownMenuItems: the menu has to
                  stay open while you tick columns and nudge them around, and a
                  menu item closes it on select. This is also the reliable way to
                  reorder — dragging a header is the shortcut, not the only
                  route. */}
              <DropdownMenuContent align="end" className="w-72 p-1">
                <DropdownMenuLabel className="trk-uppercase-label">Columns</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {pickOrder.map((key, i) => {
                  const c = byKey[key];
                  const on = visibleCols.includes(key);
                  return (
                    <div
                      key={key}
                      className="group/row flex items-center gap-1 rounded-sm pl-1 pr-0.5 py-0.5 hover:bg-zinc-50"
                    >
                      <button
                        type="button"
                        onClick={() => !c.locked && toggleCol(key)}
                        disabled={c.locked}
                        data-testid={`${testIdPrefix}-col-toggle-${key}`}
                        className={`flex flex-1 items-center gap-2 py-1 text-left text-xs ${
                          c.locked ? "cursor-not-allowed text-zinc-400" : "text-zinc-700"
                        }`}
                        title={c.locked ? "Always shown" : on ? "Hide column" : "Show column"}
                      >
                        <span className="flex w-3 shrink-0 justify-center text-[#0176D3]">
                          {on && <CheckIcon size={12} weight="bold" />}
                        </span>
                        <span className="truncate">{c.label}</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => moveCol(key, -1)}
                        disabled={i === 0}
                        aria-label={`Move ${c.label} left`}
                        title="Move left"
                        data-testid={`${testIdPrefix}-col-left-${key}`}
                        className="rounded-sm p-1 text-zinc-400 hover:bg-white hover:text-[#0176D3] disabled:opacity-25 disabled:hover:bg-transparent disabled:hover:text-zinc-400"
                      >
                        <CaretLeftIcon size={11} weight="bold" />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveCol(key, 1)}
                        disabled={i === pickOrder.length - 1}
                        aria-label={`Move ${c.label} right`}
                        title="Move right"
                        data-testid={`${testIdPrefix}-col-right-${key}`}
                        className="rounded-sm p-1 text-zinc-400 hover:bg-white hover:text-[#0176D3] disabled:opacity-25 disabled:hover:bg-transparent disabled:hover:text-zinc-400"
                      >
                        <CaretRightIcon size={11} weight="bold" />
                      </button>
                    </div>
                  );
                })}
                <DropdownMenuSeparator />
                <button
                  type="button"
                  onClick={resetLayout}
                  data-testid={`${testIdPrefix}-reset-layout`}
                  className="w-full rounded-sm px-2 py-1.5 text-left text-xs text-zinc-600 hover:bg-zinc-50"
                >
                  Reset width &amp; order
                </button>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          <Pager page={page} pageCount={pageCount} onPage={setPage} testIdPrefix={testIdPrefix} />
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="p-12 text-center" data-testid={emptyTestId}>
          <div className="trk-uppercase-label mb-2">{emptyTitle}</div>
          <p className="text-sm text-zinc-500">{emptyMessage}</p>
        </div>
      ) : (
        /*
          The header stays put while the rows scroll.

          This needs the table to own its vertical scrolling. `overflow-x: auto`
          already made this div a scroll container in BOTH axes (per spec, when
          one axis is not `visible` the other computes to `auto`), so a sticky
          <th> anchored to the page would never engage — it sticks to this
          container. Giving the container a max height turns that into the
          behaviour we want: rows scroll inside it, the header holds at its top,
          and the toolbar and pager stay outside. A short table never reaches
          the cap, so it looks exactly as before.
        */
        <div ref={scrollRef} className="overflow-auto max-h-[70vh]">
          {/* table-fixed + an explicit px width per column: that is what makes a
              resize predictable, and what lets text wrap inside a column instead
              of deciding the column's width. */}
          <table className="text-sm table-fixed" style={{ width: "max-content", minWidth: "100%" }}>
            <colgroup>
              {visible.map((c) => (
                <col key={c.key} style={{ width: widths[c.key] ? `${widths[c.key]}px` : undefined }} />
              ))}
            </colgroup>
            <thead className="sticky top-0 z-20">
              <tr className="text-left">
                {visible.map((c) => {
                  const draggable = reorderable && !c.locked;
                  const sortable = typeof c.sortValue === "function";
                  const sorted = sort.key === c.key && sortable;
                  return (
                    <th
                      key={c.key}
                      draggable={draggable && !resizingKey}
                      onDragStart={draggable ? () => setDragKey(c.key) : undefined}
                      onDragEnd={draggable ? () => { setDragKey(null); setDropKey(null); } : undefined}
                      onDragOver={reorderable ? (e) => { if (dragKey) { e.preventDefault(); setDropKey(c.key); } } : undefined}
                      onDrop={reorderable ? (e) => { e.preventDefault(); onHeaderDrop(c.key); } : undefined}
                      onClick={sortable ? () => { toggleSort(c.key, c.sortDefaultDir); setPage(1); } : undefined}
                      // bg-white, not transparent: the header sits over the rows
                      // once they scroll under it, and a see-through header
                      // renders as overlapping text. The bottom rule moves here
                      // from the <tr> for the same reason — a border on a
                      // sticky row does not travel with it in every browser.
                      // group/th is what the sort chip's hover state hangs off
                      // — a named group, so a hovered header cannot be
                      // triggered by an unrelated `group` further up the tree.
                      className={`group/th relative select-none py-3 align-bottom text-[13px] font-bold text-zinc-800 break-words bg-white border-b border-border ${
                        draggable ? "cursor-grab active:cursor-grabbing" : ""
                      } ${sortable ? "cursor-pointer hover:text-[#0176D3]" : ""} ${
                        dragKey === c.key ? "opacity-40" : ""
                      } ${c.cls || "px-4"}`}
                      title={
                        sortable
                          ? (sorted
                              ? `Sorted ${sort.dir === "asc" ? "ascending" : "descending"} — click to ${sort.dir === (c.sortDefaultDir || "asc") ? "reverse" : "clear"}`
                              : (c.sortHint || "Click to sort"))
                          : draggable ? "Drag to reorder, or use the column menu" : undefined
                      }
                      aria-sort={sorted ? (sort.dir === "asc" ? "ascending" : "descending") : undefined}
                    >
                      {/* A 2px insertion rule on the edge the column will land
                          against, rather than flooding the whole header cell
                          with colour. It follows the drag direction so it
                          actually shows where the column is going. */}
                      {dropKey === c.key && dragKey && dragKey !== c.key && (
                        <span
                          aria-hidden="true"
                          className={`pointer-events-none absolute inset-y-0 w-0.5 bg-[#0176D3] ${
                            order.indexOf(dragKey) < order.indexOf(c.key) ? "right-0" : "left-0"
                          }`}
                        />
                      )}
                      <span className="inline-flex items-baseline gap-1.5">
                        {c.head ?? c.label}
                        {/*
                          The sort affordance.

                          It was a 10px caret in zinc-300 — technically present,
                          practically invisible, so a sortable header did not
                          read as clickable at all. It is now a 16px chip: quiet
                          grey at rest, filled blue once the column is actually
                          sorting, and darkening on hover so the whole header
                          announces itself as a control.

                          self-center because this row is baseline-aligned for
                          text, and a chip has no text baseline to sit on.
                        */}
                        {sortable && (
                          <span
                            aria-hidden="true"
                            className={`self-center inline-flex h-4 w-4 items-center justify-center rounded-sm shrink-0 transition-colors ${
                              sorted
                                ? "bg-[#EFF6FF] text-[#0176D3]"
                                : "text-zinc-400 group-hover/th:text-zinc-600"
                            }`}
                          >
                            {(sorted ? sort.dir === "asc" : c.sortDefaultDir !== "desc")
                              ? <CaretUpIcon size={12} weight="bold" />
                              // Points the way the FIRST click will sort, so a
                              // descending-first column does not promise the
                              // opposite of what it does.
                              : <CaretDownIcon size={12} weight="bold" />}
                          </span>
                        )}
                      </span>
                      {resizable && (
                        // A 9px grab area sitting inside the cell's right edge —
                        // wide enough to hit comfortably, but it renders nothing
                        // except a 1px rule on hover, so the header stays clean.
                        // Kept inside the cell rather than straddling the border
                        // so the last column can't push out a scrollbar.
                        // draggable=false plus a mousedown that stops propagation
                        // keeps a resize from being taken as a reorder drag.
                        <span
                          draggable={false}
                          onMouseDown={(e) => onResizeStart(e, c.key)}
                          onClick={(e) => e.stopPropagation()}
                          // Its own tooltip, or the header's "Drag to reorder"
                          // shows while you're hovering the resize grip.
                          title="Drag to resize"
                          data-testid={`${testIdPrefix}-resize-${c.key}`}
                          className="group absolute top-0 right-0 z-10 flex h-full w-[9px] cursor-col-resize select-none justify-end"
                        >
                          <span
                            className={`w-px transition-colors duration-150 ${
                              resizingKey === c.key ? "bg-[#0176D3]" : "bg-transparent group-hover:bg-[#0176D3]"
                            }`}
                          />
                        </span>
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {pageItems.map((row, i) => (
                <tr
                  key={rowKey(row, i)}
                  data-testid={`${testIdPrefix}-row-${rowKey(row, i)}`}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  // `group` (unnamed) lets a column opt a sticky cell's background
                  // into the row's own hover tint via `group-hover:` in its `cls` —
                  // otherwise a sticky cell's own opaque background would paint
                  // over the row's hover colour and never visibly change.
                  className={`group border-b border-border last:border-0 transition-colors ${
                    onRowClick ? "cursor-pointer" : ""
                  } ${rowClassName ? rowClassName(row) : "hover:bg-zinc-50"}`}
                >
                  {visible.map((c) => (
                    <Fragment key={c.key}>
                      <td className={`py-3 break-words ${c.align || "align-top"} ${c.cls || "px-4"}`}>
                        {c.cell ? c.cell(row, i) : cellFor?.(c.key, row, i)}
                      </td>
                    </Fragment>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex items-center justify-between px-4 py-3 border-t border-border text-xs text-zinc-500">
            <span>
              Showing <span className="font-mono text-zinc-700">{(page - 1) * size + 1}{"–"}{Math.min(page * size, totalItems)}</span> of{" "}
              <span className="font-mono text-zinc-700">{totalItems}</span>
            </span>
            {footerExtra}
          </div>
        </div>
      )}
    </div>
  );
}
